// src/components/modals/EndDayModal.tsx
import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';

interface EndDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEndDay: (date: Date) => void;
}

export const EndDayModal: React.FC<EndDayModalProps> = ({ isOpen, onClose, onEndDay }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(2025, 0, 1)); // Set to January 2025 for demo

  // Default values for showcase only
  const dateStates = {
    today: new Date(2025, 0, 15), // January 15, 2025 - Today
    submitted: [
      new Date(2025, 0, 13), // January 13, 2025 - Completed
      new Date(2025, 0, 8),  // January 8, 2025 - Completed
      // new Date(2025, 0, 3),  // January 3, 2025 - Completed
    ],
    pending: [
      new Date(2025, 0, 10), // January 10, 2025 - Pending
      // new Date(2025, 0, 7),  // January 7, 2025 - Pending
      new Date(2025, 0, 14), // January 14, 2025 - Pending
    ]
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Adjust for Monday start

    const days = [];
    
    // Add previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }

    // Add next month's leading days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const getDateState = (date: Date) => {
    if (isSameDay(date, dateStates.today)) return 'today';
    if (dateStates.submitted.some(d => isSameDay(d, date))) return 'submitted';
    if (dateStates.pending.some(d => isSameDay(d, date))) return 'pending';
    return 'default';
  };

  const getDayClassName = (day: { date: Date; isCurrentMonth: boolean }) => {
    const baseClasses = 'w-10 h-10 flex items-center justify-center text-sm font-medium rounded-lg cursor-pointer transition-all duration-200';
    const state = getDateState(day.date);
    
    if (!day.isCurrentMonth) {
      return `${baseClasses} text-gray-300 hover:bg-gray-50`;
    }

    const isSelected = isSameDay(day.date, selectedDate);
    
    switch (state) {
      case 'today':
        // Today - Blue with dashed border
        return `${baseClasses} ${
          isSelected 
            ? 'bg-blue-600 text-white' 
            : 'bg-blue-500 text-white hover:bg-blue-600 hover:shadow-lg'
        } border-2  border-blue-300 `;
        
      case 'submitted':
        // Submitted/Completed - Green
        return `${baseClasses} ${
          isSelected 
            ? 'bg-green-600 text-white ring-2 ring-green-300' 
            : 'bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md border border-green-300'
        }`;
        
      case 'pending':
        // Pending - Red
        return `${baseClasses} ${
          isSelected 
            ? 'bg-red-600 text-white ring-2 ring-red-300' 
            : 'bg-red-100 text-red-800 hover:bg-red-200 hover:shadow-md border border-red-300'
        }`;
        
      default:
        // Default days
        return `${baseClasses} ${
          isSelected 
            ? 'bg-gray-600 text-white ring-2 ring-gray-300' 
            : 'text-gray-700 hover:bg-gray-100 hover:shadow-sm'
        }`;
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleEndDay = () => {
    onEndDay(selectedDate);
    onClose();
  };

  const days = getDaysInMonth(currentMonth);

  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={'xs'}>
        {/* Calendar */}
        <div className=" px-4 ">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6 bg-[#F5F5F5] rounded-md">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <h3 className="text-md font-normal text-[#737373]">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-[2px] mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-[2px]">
            {days.map((day, index) => (
              <button
                key={index}
                onClick={() => handleDateSelect(day.date)}
                className={getDayClassName(day)}
              >
                {day.date.getDate()}
              </button>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-start border-t space-x-6 mt-6 pt-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full shadow-sm"></div>
              <span className="text-xs font-semibold text-gray-700">Today</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full shadow-sm"></div>
              <span className="text-xs font-semibold text-gray-700">Submitted</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full shadow-sm"></div>
              <span className="text-xs font-semibold text-gray-700">Pending</span>
            </div>
          </div>
        </div>
    </Modal>
  );
};